import { getAppStore } from '@/store';
import uploadToCDN from '@/requests/cloud-storage/uploadToCDN';
import revokeBlobUrl from '@/utils/file/revokeBlobUrl';
import type { IMediaInput } from '@/types';

/**
 * Uploads a media file to the CDN and writes the resulting URL into the
 * input identified by `inputId`. Manages the per-input loading / error flags
 * and revokes the previous blob URL once the upload completes.
 *
 * This is a plain function (no React state / refs / effects) so it can be
 * called from anywhere — components, hooks, or background flows.
 */
const uploadMedia = async (file: File, inputId: string) => {
  const { updateInput, inputs: storeInputs } = getAppStore();

  // Get current src to clean up blob URL after upload
  const currentInput = storeInputs.find((i) => i.id === inputId);
  const currentSrc =
    currentInput && 'src' in currentInput ? currentInput.src : null;

  updateInput({
    id: inputId,
    value: {
      isLoading: true,
      hasUploadError: false,
    } as Partial<IMediaInput>,
  });

  const cdnUrl = await uploadToCDN(file);

  revokeBlobUrl(currentSrc);

  if (!cdnUrl) {
    updateInput({
      id: inputId,
      value: {
        src: null,
        isLoading: false,
        hasUploadError: true,
      } as Partial<IMediaInput>,
    });
    return null;
  }

  updateInput({
    id: inputId,
    value: {
      src: cdnUrl,
      isLoading: false,
      hasUploadError: false,
    } as Partial<IMediaInput>,
  });
  return cdnUrl;
};

export default uploadMedia;
